import { ConfigurationType, LevelType } from 'data/types';
import { getGridSpacesForLevel } from 'utils/grid';
import { generateZoneColor, getZoneSpaceShapes } from 'utils/zone-outline';

export type ThumbnailRect = {
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
};

export type ConfigurationThumbnail = {
  rects: ThumbnailRect[];
  viewBox: string;
};

const LEVEL_GAP = 60;

export function buildConfigurationThumbnail(
  config: ConfigurationType,
  structure: LevelType[]
): ConfigurationThumbnail {
  const allZones = config.condensers.flatMap((c) => c.zones);
  const zoneIdToGlobalIndex = new Map(
    allZones.map((zone, index) => [zone.uniqueId, index])
  );

  const rects: ThumbnailRect[] = [];
  let xOffset = 0;

  for (let levelIndex = 0; levelIndex < structure.length; levelIndex += 1) {
    const gridSpaces = getGridSpacesForLevel(structure, levelIndex);
    const levelRects: ThumbnailRect[] = [];
    let levelMinX = Infinity;
    let levelMaxX = -Infinity;

    config.condensers.forEach((condenser) => {
      condenser.zones.forEach((zone) => {
        if (zone.zoneItems.length === 0) return;
        const shapes = getZoneSpaceShapes(zone, gridSpaces);
        if (shapes.length === 0) return;
        const colorIndex = zoneIdToGlobalIndex.get(zone.uniqueId) ?? 0;
        const { outline } = generateZoneColor(colorIndex);
        shapes.forEach((shape) => {
          levelRects.push({
            x: shape.position.x,
            y: shape.position.y,
            width: shape.position.width,
            height: shape.position.height,
            color: outline
          });
          levelMinX = Math.min(levelMinX, shape.position.x);
          levelMaxX = Math.max(
            levelMaxX,
            shape.position.x + shape.position.width
          );
        });
      });
    });

    if (levelRects.length > 0) {
      const shift = xOffset - levelMinX;
      levelRects.forEach((rect) => {
        rects.push({ ...rect, x: rect.x + shift });
      });
      xOffset += levelMaxX - levelMinX + LEVEL_GAP;
    }
  }

  if (rects.length === 0) {
    return { rects: [], viewBox: '0 0 1 1' };
  }

  const minX = Math.min(...rects.map((r) => r.x));
  const minY = Math.min(...rects.map((r) => r.y));
  const maxX = Math.max(...rects.map((r) => r.x + r.width));
  const maxY = Math.max(...rects.map((r) => r.y + r.height));
  const width = maxX - minX || 1;
  const height = maxY - minY || 1;

  return {
    rects,
    viewBox: `${minX} ${minY} ${width} ${height}`
  };
}
